/**
 * Step 3 "Your estimate" (customer-facing). One focal point: the savings
 * number. Never renders rep-only values (VPP) or rep notices.
 */
import { TERM_OPTIONS, type TermMonths } from "../../config";
import type { BidResult } from "../../engine/bid";
import { monthlyBars } from "../chart";
import type { AppState } from "../state";
import { MONTHS_LONG, MONTHS_SHORT, termStartYear, termView, usd, type TermView } from "../viewmodel";

export const VALUE_PROPS: readonly string[] = ["100% renewable", "$0 base fee", "Fixed rate", "4.6 on Trustpilot"];

export function valuePropsHtml(): string {
  return `<p class="props">${VALUE_PROPS.join('<span aria-hidden="true"> · </span>')}</p>`;
}

/** Savings breakdown rows. Returns "" when there is only one step (it would repeat the hero). */
export function breakdownHtml(v: TermView): string {
  if (v.waterfall.length < 2) return "";
  const rows = v.waterfall
    .map((r) => `<li><span>${r.label}</span><span class="amt${r.total < 0 ? " neg" : ""}">${r.total < 0 ? "−" : ""}${usd(Math.abs(r.avg))}/mo</span></li>`)
    .join("");
  return `<section class="breakdown" aria-labelledby="h-breakdown">
    <h2 class="section-label" id="h-breakdown">Breakdown</h2>
    <ul>${rows}<li class="total"><span>Total</span><span class="amt">${usd(v.savingsAvg)}/mo</span></li></ul>
  </section>`;
}

export function includedHtml(state: AppState): string {
  if (!state.battery.enabled) return "";
  return `<p class="included">Octopus Battery (${state.battery.capacityKwh} kWh), <span class="free">included free</span></p>`;
}

/** Customer-facing hero copy shared by screen and print. */
export function heroCopy(v: TermView): { label: string; amount: string; positive: boolean } {
  const positive = v.savingsTotal > 0;
  return positive
    ? { label: "Estimated savings", amount: usd(v.savingsAvg), positive }
    : { label: v.savingsTotal < 0 ? "Estimated extra cost" : "Estimated difference", amount: usd(Math.abs(v.savingsAvg)), positive };
}

export interface BidViewOptions {
  /** Whether the "Monthly detail" disclosure is open. */
  chartOpen?: boolean;
  today?: Date;
}

export function renderBid(root: HTMLElement, bid: BidResult, state: AppState, opts: BidViewOptions = {}): void {
  const v = termView(bid, state.term);
  const hero = heroCopy(v);
  const today = opts.today ?? new Date();

  const startOptions = MONTHS_SHORT.map((m, i) => {
    const y = termStartYear(i, today);
    return `<option value="${i}"${i === state.startMonth ? " selected" : ""}>${m} ${y}</option>`;
  }).join("");

  const terms = TERM_OPTIONS.map(
    (t: TermMonths) =>
      `<label><input type="radio" name="term" value="${t}" data-bind="term"${t === state.term ? " checked" : ""} /><span>${t} months</span></label>`,
  ).join("");

  root.innerHTML = `
  <div class="estimate">
    <section class="hero" aria-labelledby="hero-label">
      <p class="hero-label" id="hero-label">${hero.label}</p>
      <p class="hero-num${hero.positive ? "" : " neutral"}">${hero.amount}<span class="hero-unit">/mo</span></p>
      <p class="hero-line">${usd(Math.abs(v.savingsTotal))} over ${v.term} months · starting
        <label class="visually-hidden" for="in-start">Plan start month</label>
        <select id="in-start" class="inline-select" data-bind="startMonth">${startOptions}</select></p>
      <div class="seg seg-sm term-seg" role="radiogroup" aria-label="Plan length">${terms}</div>
    </section>

    <div class="compare">
      <div class="tile"><span class="tile-label">Today</span><span class="tile-val muted">${usd(v.todayAvg)}<small>/mo</small></span></div>
      <div class="tile"><span class="tile-label">With Octopus</span><span class="tile-val">${usd(v.octopusAvg)}<small>/mo</small></span></div>
    </div>

    ${includedHtml(state)}
    ${breakdownHtml(v)}

    <details class="detail" id="chart-detail"${opts.chartOpen ? " open" : ""}>
      <summary>Monthly detail</summary>
      <div class="legend"><span class="key today"></span>Today <span class="key octo"></span>Octopus</div>
      ${monthlyBars({ months: v.months, today: v.todayMonths, octopus: v.octopusMonths, title: `Monthly bill, today vs Octopus, ${v.term} months starting ${MONTHS_LONG[state.startMonth]}` })}
    </details>

    ${valuePropsHtml()}
  </div>`;
}
